import React, {useCallback} from "react";
import Compressor from "compressorjs";
import Camera from "react-html5-camera-photo";
import "react-html5-camera-photo/build/css/index.css";
import {useThemeMode} from "../../../../_metronic/partials";
import { generateMultiSignedUrls } from "../../../models/_publicCall";
import { FOLDER_NAME } from "../../../../_metronic/helpers/enums";
import toast from "react-hot-toast";
import { isHeic, heicTo } from "heic-to";

type UploadFile = { name: string; type: string; url: string };

interface UploadBoxProps {
  label?: string;
  files: UploadFile[];
  onFilesChange: (files: UploadFile[]) => void;
  enableCamera?: boolean;
  accept?: string;
}

const UploadBox: React.FC<UploadBoxProps> = ({
  label,
  files,
  onFilesChange,
  enableCamera = true,
  accept = "image/*,video/*",
}) => {
  const {mode} = useThemeMode();
  const [showCapture, setShowCapture] = React.useState(false);
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [facingMode, setFacingMode] = React.useState<"user" | "environment">("user");
  const [isUploading, setIsUploading] = React.useState(false);

  // Sync helper for quick file extension/MIME type check (for filtering)
  const looksLikeHeicFile = (file: File): boolean => {
    return (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heic") ||
      file.name.toLowerCase().endsWith(".heif")
    );
  };

  // Async check using the library's isHeic function (for actual conversion)
  const isHeicFile = async (file: File): Promise<boolean> => {
    // Use the library's isHeic function first
    try {
      const result = await isHeic(file);
      if (result) return true;
    } catch (e) {
      // Fall back to file extension/type check if library check fails
    }
    // Fallback: check file extension and MIME type
    return looksLikeHeicFile(file);
  };

  const convertHeicToJpeg = async (file: File): Promise<File> => {
    try {
      // Convert HEIC to JPEG using heic-to
      const jpegBlob = await heicTo({
        blob: file,
        type: "image/jpeg",
        quality: 1.0,
      });
      
      if (!jpegBlob || jpegBlob.size === 0) {
        throw new Error("HEIC conversion returned empty result");
      }
      
      // Create a File from the JPEG Blob
      const convertedFile = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
      
      return convertedFile;
    } catch (error: any) {
      console.error("HEIC conversion error:", error);
      throw new Error(`Failed to convert HEIC file: ${error?.message || error}`);
    }
  };

  const compressImage = (file: File): Promise<Blob> =>
    new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.4,
        maxWidth: 1920,
        maxHeight: 1920,
        convertSize: 1000000,
        mimeType: "image/jpeg",
        success(result) {
          resolve(result);
        },
        error(err) {
          reject(err);
        },
      });
    });

  const requestSignedUrl = async (name: string, type: string) => {
    const items = await generateMultiSignedUrls([
      { file_name: name, file_type: type, folder_name: FOLDER_NAME.INSTALLATION_JOB_MEDIA },
    ]);
    return items?.[0]?.url || "";
  };

  const uploadToSignedUrl = async (signedUrl: string, fileOrBlob: Blob, type: string) => {
    const res = await fetch(signedUrl, {
      method: "PUT",
      headers: { "Content-Type": type },
      body: fileOrBlob,
    });
    if (!res.ok) throw new Error("Upload failed");
    return signedUrl.split("?")[0];
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const fileList = input.files;
    if (!fileList) return;
    
    const validFiles = Array.from(fileList).filter(file => 
      file.type.startsWith("image/") || 
      file.type.startsWith("video/") ||
      looksLikeHeicFile(file)
    );
    
    if (validFiles.length === 0) {
      toast.error("Please select image or video files only.");
      input.value = "";
      return;
    }

    setIsUploading(true);
    const next: UploadFile[] = [...files];
    
    try {
      // Process all files first (convert HEIC, then compress images)
      const processedFiles: File[] = [];
      for (const file of validFiles) {
        try {
          let outFile = file;
          const fileIsHeic = await isHeicFile(file);

          if (file.type.startsWith("image/") || fileIsHeic) {
            if (fileIsHeic) {
              outFile = await convertHeicToJpeg(file);
            }
            // Compress the image (now guaranteed to be JPEG if it was HEIC)
            const compressed = await compressImage(outFile);
            outFile = new File([compressed], outFile.name, {type: "image/jpeg"});
          }
          processedFiles.push(outFile);
        } catch (e) {
          console.error(`UploadBox: error processing ${file.name}`, e);
          toast.error(`Failed to process ${file.name}. Please try again.`);
        }
      }

      if (processedFiles.length === 0) {
        input.value = "";
        return;
      }

      const existingNames = new Set(files.map((f) => f.name));
      const uniqueFiles: File[] = [];
      const skippedDuplicateNames: string[] = [];
      for (const file of processedFiles) {
        if (existingNames.has(file.name) || uniqueFiles.some((f) => f.name === file.name)) {
          skippedDuplicateNames.push(file.name);
          continue;
        }
        uniqueFiles.push(file);
        existingNames.add(file.name);
      }

      if (uniqueFiles.length === 0) {
        if (skippedDuplicateNames.length > 0) {
          toast.error(`Skipped duplicate file(s): ${Array.from(new Set(skippedDuplicateNames)).join(", ")}`);
        }
        input.value = "";
        return;
      }

      // Get all signed URLs in a single API call
      const signedUrlRequests = uniqueFiles.map(file => ({
        file_name: file.name,
        file_type: file.type,
        folder_name: FOLDER_NAME.INSTALLATION_JOB_MEDIA,
      }));
      
      const signedUrls = await generateMultiSignedUrls(signedUrlRequests);
      
      // Upload each file to its corresponding signed URL
      for (let i = 0; i < uniqueFiles.length; i++) {
        const file = uniqueFiles[i];
        const signedUrl = signedUrls?.[i]?.url;
        
        if (!signedUrl) {
          console.error(`No signed URL returned for ${file.name}`);
          toast.error(`Failed to get upload URL for ${file.name}. Please try again.`);
          continue;
        }
        
        try {
          const publicUrl = await uploadToSignedUrl(signedUrl, file, file.type);
          next.push({name: file.name, type: file.type, url: publicUrl});
        } catch (e) {
          console.error(`UploadBox: error uploading ${file.name}`, e);
          toast.error(`Failed to upload ${file.name}. Please try again.`);
        }
      }

      if (skippedDuplicateNames.length > 0) {
        toast.error(`Skipped duplicate file(s): ${Array.from(new Set(skippedDuplicateNames)).join(", ")}`);
      }
    } finally {
      setIsUploading(false);
      onFilesChange(next);
      input.value = "";
    }
  };

  const removeFile = useCallback(
    (fileUrl: string) => {
      onFilesChange(files.filter((f) => f.url !== fileUrl));
    },
    [files, onFilesChange]
  );

  const handleTakePhoto = async (dataUri: string) => {
    const name = `captured_${Date.now()}.jpg`;
    try {
      setIsUploading(true);
      // Convert data URI to Blob for upload
      const res = await fetch(dataUri);
      const blob = await res.blob();
      const signedUrl = await requestSignedUrl(name, "image/jpeg");
      if (!signedUrl) throw new Error("No signed URL returned");
      const publicUrl = await uploadToSignedUrl(signedUrl, blob, "image/jpeg");
      onFilesChange([...files, {name, type: "image/jpeg", url: publicUrl}]);
    } catch (e) {
      console.error("UploadBox: failed to upload captured photo", e);
      toast.error("Failed to upload captured photo. Please try again.");
    } finally {
      setIsUploading(false);
      setShowCapture(false);
      setCameraError(null);
    }
  };

  return (
    <div className="upload-section">
      {label && <h3 className="mb-2">{label}</h3>}
      <div className={`upload-box ${mode === "dark" ? "dark-upload-box" : ""}`}>
        <input
          type="file"
          className="file-input"
          multiple
          accept={accept}
          onChange={handleFileChange}
        />

        {files.length > 0 ? (
          <div className="preview-container d-flex flex-wrap mt-3">
            {files.map((file, index) => (
              <div key={index} className="img-box mt-3 ms-2 justify-content-center">
                {file.type.startsWith("image/") ? (
                  <img
                    src={file.url}
                    alt={file.name}
                    width={150}
                    className="img-wrapper overflow-hidden m-2 ms-0 d-flex position-relative"
                  />
                ) : (
                  <video
                    controls
                    src={file.url}
                    width={170}
                    className="img-wrapper overflow-hidden m-2 ms-0 d-flex position-relative"
                  />
                )}
                <button type="button" onClick={() => removeFile(file.url)} className="close-btn img-btn">
                  <i className="fa fa-close fs-5 p-1"></i>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="file-label text-center p-3">
            <i className="fa fa-cloud-upload cloud-icon fs-3"></i>
            <span className="d-block mt-2">Browse Files</span>
            <p className="text-muted">Drag and drop files here</p>
          </div>
        )}
        {isUploading && (
          <div className="d-flex align-items-center mt-2">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span>Uploading...</span>
          </div>
        )}
      </div>

      {enableCamera && (
        <div className="mt-3">
          {!showCapture && (
            <button type="button" className="btn btn-primary" onClick={() => setShowCapture(true)}>
              Capture Image
            </button>
          )}
          {showCapture && (
            <>
              {cameraError && (
                <div className="alert alert-danger mt-2" role="alert">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  {cameraError}
                </div>
              )}
              <Camera
                onTakePhoto={handleTakePhoto}
                onCameraError={() => setCameraError("Camera error")}
                idealFacingMode={facingMode}
                isFullscreen={false}
                imageType="png"
              />
              <div className="mt-3 d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setFacingMode((p) => (p === "user" ? "environment" : "user"))}
                >
                  Switch to {facingMode === "user" ? "Back" : "Front"} Camera
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setShowCapture(false);
                    setCameraError(null);
                  }}
                >
                  Dismiss
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadBox;